import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

// React-shared.
import Modal from '@exonet/react-shared/lib/Elements/Modal';

// App components.
import Button from 'app/client/components/Button';
import ApiError from '../ApiError';

/**
 * Based on the given header, content and footer method the modal is rendered.
 *
 * @param {Function} onClose The method to fire when the modal is closed.
 * @param {Function|boolean} onCancel The method to fire when the modal is cancelled.
 * @param {Function} onSubmit The method to fire when the modal is confirmed.
 * @param {Function|boolean} onSuccess The method to fire when the modal is confirmed.
 * @param {Element|Element[]} children The children to render.
 * @param {Object} other The other props passed through to the <Modal>.
 *
 * @returns {Element} The rendered modal.
 */
const Confirm = ({
  onSubmit,
  onSuccess = false,
  onClose = false,
  onCancel = false,
  children = null,
  withResult = false,
  ...other
}) => {
  const { t } = useTranslation();
  const [cancelled, setCancelled] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState(undefined);

  const handleNo = useCallback(() => {
    setCancelled(false);
    if (onCancel !== false) {
      onCancel();
      return;
    }

    onClose();
  }, [onCancel, onClose, setCancelled]);

  const handleYes = useCallback(async () => {
    setSubmitted(true);
    const result = await onSubmit();

    if (result?.errors) {
      setErrors(result.errors);
    }

    if (onSuccess !== false) {
      if (withResult) {
        onSuccess(result);
        onClose();

        return;
      }

      onSuccess();
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onSubmit, setSubmitted, onSuccess]);

  return (
    <Modal
      {...other}
      onClose={onClose}
      footer={
        <>
          <Button onClick={handleNo} loading={cancelled} disabled={cancelled || submitted}>
            {t('core:common.no')}
          </Button>
          <Button onClick={handleYes} loading={submitted} disabled={cancelled || submitted} primary>
            {t('core:common.yes')}
          </Button>
        </>
      }
    >
      {children}
      {errors && <ApiError errors={errors} />}
    </Modal>
  );
};

Confirm.propTypes = {
  onClose: PropTypes.func.isRequired,
  onCancel: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
  onSubmit: PropTypes.func.isRequired,
  onSuccess: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
  withResult: PropTypes.bool,
  children: PropTypes.node,
};

export default Confirm;
